import { useEffect, useRef, useState, useCallback } from 'react';
import { Bell, Check, Trash2, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { ReservationDialog } from './ReservationDialogs';

const TYPE_LABEL = {
  reservation_new: 'Neue Reservierung',
  reservation_changed: 'Geändert',
  reservation_cancelled: 'Storniert',
  reservation_customer_cancelled: 'Gast-Stornierung',
  reservation_no_show: 'No-Show',
  waitlist_new: 'Warteliste',
};

const TYPE_COLOR = {
  reservation_new: '#3B82F6',
  reservation_changed: '#EAB308',
  reservation_cancelled: '#9CA3AF',
  reservation_customer_cancelled: '#EF4444',
  reservation_no_show: '#EF4444',
  waitlist_new: '#22C55E',
};

function formatRelative(iso) {
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'gerade eben';
    if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min.`;
    if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std.`;
    return d.toLocaleDateString('de-DE');
  } catch {
    return '';
  }
}

// Swipe-to-delete wrapper (touch devices only: mobile/tablet). A left swipe past
// the threshold slides the row out and deletes it; a short tap still opens it.
// Desktop keeps the existing hover action buttons untouched.
function SwipeableNotification({ id, clickable, onOpen, onDelete, children }) {
  const [dx, setDx] = useState(0);
  const startX = useRef(0);
  const startY = useRef(0);
  const dragging = useRef(false);
  const moved = useRef(false);
  const THRESHOLD = 80;

  const onTouchStart = (e) => {
    const t = e.touches[0];
    startX.current = t.clientX;
    startY.current = t.clientY;
    dragging.current = true;
    moved.current = false;
  };
  const onTouchMove = (e) => {
    if (!dragging.current) return;
    const t = e.touches[0];
    const deltaX = t.clientX - startX.current;
    const deltaY = t.clientY - startY.current;
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 6) {
      moved.current = true;
      setDx(Math.min(0, deltaX)); // only allow left swipe
    }
  };
  const onTouchEnd = () => {
    dragging.current = false;
    if (dx <= -THRESHOLD) {
      setDx(-window.innerWidth);
      setTimeout(() => onDelete(id), 180);
    } else {
      setDx(0);
    }
  };
  const handleClick = () => {
    if (moved.current) return; // it was a swipe, not a tap
    if (clickable) onOpen();
  };

  return (
    <div className="relative overflow-hidden border-b border-[#E5E5E0]" data-testid={`notif-swipe-${id}`}>
      <div
        className="absolute inset-0 flex items-center justify-end pr-6 bg-[#EF4444] transition-opacity"
        style={{ opacity: dx < -4 ? 1 : 0 }}
        aria-hidden="true"
      >
        <Trash2 className="w-5 h-5 text-white" />
      </div>
      <div
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onClick={handleClick}
        style={{ transform: `translateX(${dx}px)`, transition: dragging.current ? 'none' : 'transform 0.18s ease', touchAction: 'pan-y' }}
        className="relative bg-white"
      >
        {children}
      </div>
    </div>
  );
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const navigate = useNavigate();

  // Inline reservation dialog (opened directly from the bell instead of
  // navigating to the heavy floor-plan view — faster, especially on mobile).
  const [selectedResv, setSelectedResv] = useState(null);
  const [resvOpen, setResvOpen] = useState(false);
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const metaLoadedRef = useRef(false);

  const ensureMeta = useCallback(async () => {
    if (metaLoadedRef.current) return;
    try {
      const [a, t] = await Promise.all([api.get('/areas'), api.get('/tables')]);
      setAreas(a.data || []);
      setTables(t.data || []);
      metaLoadedRef.current = true;
    } catch {}
  }, []);

  const loadCount = useCallback(async () => {
    try {
      const r = await api.get('/notifications/unread-count');
      setCount(r.data.count || 0);
    } catch {}
  }, []);

  const loadItems = useCallback(async () => {
    try {
      const r = await api.get('/notifications', { params: { limit: 30 } });
      setItems(r.data || []);
    } catch {}
  }, []);

  useEffect(() => {
    loadCount();
    const t = setInterval(loadCount, 30000);
    return () => clearInterval(t);
  }, [loadCount]);

  useEffect(() => {
    if (open) loadItems();
  }, [open, loadItems]);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const markRead = async (id) => {
    await api.post(`/notifications/${id}/read`);
    setItems(items.map((i) => (i.id === id ? { ...i, read: true } : i)));
    loadCount();
  };

  const remove = async (id) => {
    await api.delete(`/notifications/${id}`);
    setItems(items.filter((i) => i.id !== id));
    loadCount();
  };

  const markAllRead = async () => {
    await api.post('/notifications/read-all');
    setItems(items.map((i) => ({ ...i, read: true })));
    loadCount();
  };

  // Open a reservation notification directly as an inline dialog (no navigation
  // to the floor plan — avoids the heavy re-render/lag, especially on mobile).
  const openReservation = async (n) => {
    setOpen(false);
    if (!n.read) markRead(n.id);
    try {
      await ensureMeta();
      const r = await api.get(`/reservations/${n.entity_id}`);
      setSelectedResv(r.data);
      setResvOpen(true);
    } catch {
      navigate('/admin/reservations');
    }
  };

  // After a status/edit change in the inline dialog: refresh the reservation,
  // the unread count, and let any open admin page know it should reload.
  const handleResvUpdate = useCallback(async () => {
    loadCount();
    window.dispatchEvent(new CustomEvent('reservations-changed'));
    if (selectedResv?.id) {
      try {
        const r = await api.get(`/reservations/${selectedResv.id}`);
        setSelectedResv(r.data);
      } catch {}
    }
  }, [loadCount, selectedResv]);

  // Tapping anywhere on a notification opens the relevant target:
  // reservation → details dialog (via floor plan), waitlist → waitlist page.
  const handleItemClick = (n) => {
    if (n.entity_type === 'reservation' && n.entity_id) {
      openReservation(n);
    } else if (n.entity_type === 'waitlist') {
      if (!n.read) markRead(n.id);
      setOpen(false);
      navigate('/admin/waitlist');
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notif-bell-btn"
        className="relative w-9 h-9 flex items-center justify-center rounded-full hover:bg-[#F9F9F7] transition-colors text-[#5C5C59] hover:text-[#1C1C19]"
        aria-label="Benachrichtigungen"
      >
        <Bell className="w-5 h-5" />
        {count > 0 && (
          <span
            data-testid="notif-bell-badge"
            className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#EF4444] text-white text-[10px] font-bold flex items-center justify-center"
          >
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="notif-bell-panel"
          className="fixed left-2 right-2 top-[64px] w-auto max-w-none sm:absolute sm:left-auto sm:right-0 sm:top-auto sm:mt-2 sm:w-[340px] sm:max-w-[92vw] lg:left-0 lg:right-auto bg-white border border-[#E5E5E0] rounded-lg shadow-xl z-50 overflow-hidden"
        >
          <div className="px-4 py-3 border-b border-[#E5E5E0] flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-[#1C1C19]">Benachrichtigungen</p>
              <p className="text-[11px] text-[#5C5C59]">{count} ungelesen</p>
            </div>
            <div className="flex items-center gap-1">
              {count > 0 && (
                <button
                  onClick={markAllRead}
                  data-testid="notif-mark-all-read"
                  className="text-[11px] text-[#5C5C59] hover:text-[#1C1C19] px-2 py-1 rounded"
                >
                  Alle als gelesen
                </button>
              )}
              <button onClick={() => setOpen(false)} className="p-1 text-[#5C5C59] hover:text-[#1C1C19]">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {items.length === 0 && (
              <div className="px-4 py-12 text-center text-sm text-[#5C5C59]" data-testid="notif-empty">
                Keine Benachrichtigungen
              </div>
            )}
            {items.map((n) => {
              const clickable = (n.entity_type === 'reservation' && n.entity_id) || n.entity_type === 'waitlist';
              return (
              <SwipeableNotification
                key={n.id}
                id={n.id}
                clickable={clickable}
                onOpen={() => handleItemClick(n)}
                onDelete={remove}
              >
              <div
                data-testid={`notif-item-${n.id}`}
                role={clickable ? 'button' : undefined}
                tabIndex={clickable ? 0 : undefined}
                className={`group px-4 py-3 flex gap-3 hover:bg-[#F9F9F7] ${
                  clickable ? 'cursor-pointer' : ''
                } ${n.read ? 'opacity-70' : ''}`}
              >
                <div
                  className="w-2 h-2 rounded-full flex-shrink-0 mt-2"
                  style={{ background: n.read ? '#E5E5E0' : TYPE_COLOR[n.type] || '#5C5C59' }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[10px] uppercase tracking-wider text-[#5C5C59]">
                      {TYPE_LABEL[n.type] || n.type}
                    </span>
                    <span className="text-[10px] text-[#5C5C59]">{formatRelative(n.created_at)}</span>
                  </div>
                  <p
                    className="text-sm font-medium text-[#1C1C19] mt-0.5"
                    data-testid={n.entity_type === 'reservation' && n.entity_id ? `notif-open-resv-${n.id}` : undefined}
                  >
                    {n.title}
                  </p>
                  <p className="text-xs text-[#5C5C59] mt-0.5">{n.message}</p>
                </div>
                <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {!n.read && (
                    <button
                      onClick={(e) => { e.stopPropagation(); markRead(n.id); }}
                      data-testid={`notif-mark-read-${n.id}`}
                      className="p-1 text-[#5C5C59] hover:text-[#22C55E]"
                      title="Als gelesen markieren"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(n.id); }}
                    data-testid={`notif-delete-${n.id}`}
                    className="p-1 text-[#5C5C59] hover:text-[#EF4444]"
                    title="Entfernen"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
              </SwipeableNotification>
              );
            })}
          </div>
        </div>
      )}

      <ReservationDialog
        open={resvOpen}
        onClose={() => setResvOpen(false)}
        reservation={selectedResv}
        tables={tables}
        areas={areas}
        onUpdate={handleResvUpdate}
      />
    </div>
  );
}
